/**
 * Schemas para rtbf_requests tab + state machine 2-person rule.
 *
 * Per FWA-SEC-024 G0 + data-model V1.1 §3.13a + privacy.md §4.6.
 *
 * Privacy: razones/motivos NUNCA en audit summaries (solo entity_ids + hashes).
 */
import { z } from 'zod';
import { UlidSchema, VersionedFieldsSchema } from './common.js';

// ─── Enums ─────────────────────────────────────────────────────────────────

export const RtbfStatusSchema = z.enum([
  'pending-approval',
  'approved',
  'rejected',
  'soft-deleted',
  'executed',
  'cancelled',
]);
export type RtbfStatus = z.infer<typeof RtbfStatusSchema>;

export const RtbfTutorVerificationSchema = z.enum([
  'verified-phone-call',
  'verified-in-person',
  'pending-verification',
]);
export type RtbfTutorVerification = z.infer<typeof RtbfTutorVerificationSchema>;

// ─── Master schema `rtbf_requests` (V1.1 §3.13a) ───────────────────────────

export const RtbfRequestSchema = VersionedFieldsSchema.extend({
  nina_entity_id: UlidSchema,
  requested_by_tutor_verification: RtbfTutorVerificationSchema,
  request_reason: z.string().min(20).max(500),
  status: RtbfStatusSchema,
  approved_by: z.string().email().nullable(),
  approval_reason: z.string().max(500).nullable(),
  approved_at: z.string().datetime().nullable(),
  rejected_at: z.string().datetime().nullable(),
  rejection_reason: z.string().max(500).nullable(),
  soft_deleted_at: z.string().datetime().nullable(),
  executed_at: z.string().datetime().nullable(),
  cancelled_at: z.string().datetime().nullable(),
  cancelled_by: z.string().email().nullable(),
  tier_actions_json: z.record(z.string(), z.array(z.string())).nullable(),
  audit_chain_event_ids: z.array(z.string()).default([]),
});
export type RtbfRequest = z.infer<typeof RtbfRequestSchema>;

// ─── Payloads ──────────────────────────────────────────────────────────────

export const RtbfCreatePayloadSchema = z.object({
  nina_entity_id: UlidSchema,
  requested_by_tutor_verification: RtbfTutorVerificationSchema,
  request_reason: z.string().min(20).max(500),
});
export type RtbfCreatePayload = z.infer<typeof RtbfCreatePayloadSchema>;

export const RtbfApprovalPayloadSchema = z.object({
  approval_reason: z.string().max(500).optional(),
});
export type RtbfApprovalPayload = z.infer<typeof RtbfApprovalPayloadSchema>;

export const RtbfCancelPayloadSchema = z.object({
  cancellation_reason: z.string().max(500).optional(),
});
export type RtbfCancelPayload = z.infer<typeof RtbfCancelPayloadSchema>;

export const RtbfRejectPayloadSchema = z.object({
  rejection_reason: z.string().min(10).max(500),
});
export type RtbfRejectPayload = z.infer<typeof RtbfRejectPayloadSchema>;

// ─── State machine validation ─────────────────────────────────────────────

const ALLOWED_TRANSITIONS: Record<RtbfStatus, RtbfStatus[]> = {
  'pending-approval': ['approved', 'rejected', 'cancelled'],
  approved: ['soft-deleted'], // automático al approve (mismo write transaction)
  'soft-deleted': ['executed', 'cancelled'],
  rejected: [],
  executed: [],
  cancelled: [],
};

export interface RtbfStateTransitionInput {
  current: RtbfStatus;
  target: RtbfStatus;
  current_created_by: string;
  actor_email: string; // El que hace la transition
  current_status: RtbfStatus; // alias for current — explicit
}

export interface RtbfStateTransitionResult {
  valid: boolean;
  reason: string | null;
}

/**
 * Valida transition de state machine RTBF.
 *
 * Reglas:
 * - Transition debe estar en ALLOWED_TRANSITIONS
 * - approve REQUIRE actor_email !== current_created_by (2-person rule per
 *   privacy.md §4.6)
 * - cancel desde soft-deleted REQUIRE actor_email !== current_created_by (mismo
 *   2-person rule per privacy.md §4.6 — cancel reversal needs second person)
 *
 * V1 single-human-multi-role: el mismo humano puede actuar como ambos roles
 * pero audit captura el role-switch explícitamente (V2 strict enforcement).
 */
export function validateRtbfStateTransition(
  input: RtbfStateTransitionInput,
): RtbfStateTransitionResult {
  const { current, target, actor_email, current_created_by } = input;
  // En RTBF las operaciones son explícitas (approve/reject/cancel/execute) —
  // calling con target=current significa "intentar operación inválida sobre
  // estado terminal" (e.g., rechazar lo ya-rechazado). Bloqueamos no-op.
  const allowed = ALLOWED_TRANSITIONS[current] ?? [];
  if (!allowed.includes(target)) {
    return {
      valid: false,
      reason: `Invalid RTBF state transition: ${current} → ${target}`,
    };
  }
  // 2-person rule: approve y cancel-from-soft-deleted requieren approver != creator
  if (target === 'approved' && actor_email.toLowerCase() === current_created_by.toLowerCase()) {
    return {
      valid: false,
      reason: '2-person rule violation: approver cannot be the same as creator',
    };
  }
  if (
    target === 'cancelled' &&
    current === 'soft-deleted' &&
    actor_email.toLowerCase() === current_created_by.toLowerCase()
  ) {
    return {
      valid: false,
      reason: '2-person rule violation: cancel from soft-deleted cannot be by creator',
    };
  }
  return { valid: true, reason: null };
}
